import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Layout, Avatar, Typography, Button, Badge, Dropdown, Grid, Spin } from 'antd';
import { UserOutlined, EditOutlined, LogoutOutlined, MenuOutlined } from '@ant-design/icons';
import { Moon, Sun } from 'lucide-react';
import { ensureDataUrl, formatDbActualDate, formatWipoDatesDisplay } from '../../utils/helpers';
import { useTheme } from '../../theme/ThemeProvider';

const { Header } = Layout;
const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const AppHeader = ({
  user,
  profile,
  socketStatus,
  onOpenChangelog,
  onLogout,
  onOpenCabinet,
  showMobileMenu,
  onOpenMobileMenu,
}) => {
  const { darkMode, toggleDarkMode } = useTheme();
  const screens = useBreakpoint();
  const isWide = !!screens.lg;
  const compactToolbar = !screens.sm;
  const dropdownTrigger = isWide ? ['hover'] : ['click'];
  const statusText =
    socketStatus === 'connected' ? 'Онлайн' : socketStatus === 'connecting' ? 'Подключение…' : 'Нет соединения';

  const dbActualDates = useSelector((s) => s.interface.dbActualDates);
  const datesLoading = socketStatus === 'connected' && dbActualDates == null;
  const tm = formatDbActualDate(dbActualDates?.tmRfDate) || '—';
  const wipo = formatWipoDatesDisplay(dbActualDates?.wipoDates);
  const app = formatDbActualDate(dbActualDates?.appRfDate) || '—';
  const datesTitle = useMemo(
    () => `Товарные знаки: ${tm} · ВИПО: ${wipo} · Заявки: ${app}`,
    [tm, wipo, app]
  );

  return (
    <Header
      className="app-main-header"
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: compactToolbar ? 4 : 8,
        minHeight: 48,
        height: 'auto',
        padding: compactToolbar ? '4px 6px' : '6px 12px',
        backgroundColor: '#001529',
        zIndex: 1000,
      }}
    >
      {/* Симметричные поля — слева бургер на узком экране; заголовок по центру */}
      <div
        style={{
          flex: '1 1 0',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        {showMobileMenu && (
          <Button
            type="text"
            icon={<MenuOutlined style={{ color: 'white', fontSize: 16 }} />}
            onClick={onOpenMobileMenu}
            aria-label="Открыть меню"
          />
        )}
      </div>
      <div className="app-header-title-wrap">
        <Title level={4} className="app-header-title" style={{ color: 'white', margin: 0 }}>
          {compactToolbar ? 'Поиск товарных знаков' : 'Сервис поиска товарных знаков'}
        </Title>
        <div
          className="app-header-db-dates-line"
          title={datesLoading ? undefined : datesTitle}
          aria-label={datesLoading ? 'Загрузка дат актуализации' : datesTitle}
        >
          {datesLoading ? (
            <Spin size="small" />
          ) : (
            <>
              <span className="app-header-db-dates-line__abbr">ТЗ</span>
              {tm}
              <span className="app-header-db-dates-line__sep" aria-hidden>
                {' '}
                ·{' '}
              </span>
              <span className="app-header-db-dates-line__abbr">ВИПО</span>
              {wipo}
              <span className="app-header-db-dates-line__sep" aria-hidden>
                {' '}
                ·{' '}
              </span>
              <span className="app-header-db-dates-line__abbr">Заявки</span>
              {app}
            </>
          )}
        </div>
      </div>
      <div
        style={{
          flex: '1 1 0',
          minWidth: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: compactToolbar ? 4 : 8,
          color: 'white',
        }}
      >
        <Button
          type="text"
          icon={<EditOutlined style={{ color: 'white' }} />}
          size="small"
          onClick={onOpenChangelog}
        />
        <Badge
          status={socketStatus === 'connected' ? 'success' : socketStatus === 'connecting' ? 'processing' : 'error'}
          text={isWide ? statusText : undefined}
          style={{ color: 'white' }}
        />
        <Button
          type="text"
          icon={
            darkMode ? (
              <Moon size={16} style={{ color: 'white' }} />
            ) : (
              <Sun size={16} style={{ color: 'white' }} />
            )
          }
          size="small"
          onClick={toggleDarkMode}
          aria-label={darkMode ? 'Светлая тема' : 'Тёмная тема'}
        />
        <Dropdown
          menu={{
            items: [
              {
                key: 'profile',
                icon: <UserOutlined />,
                label: 'Профиль',
                onClick: onOpenCabinet
              },
              {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: 'Выйти',
                onClick: onLogout
              }
            ]
          }}
          trigger={dropdownTrigger}
          placement="bottomRight"
          overlayStyle={{ zIndex: 1001 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: compactToolbar ? 4 : 8,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <Avatar
              size="small"
              key={profile?.avatarDataUrl || 'no-avatar'}
              src={ensureDataUrl(profile?.avatarDataUrl)}
              icon={!profile?.avatarDataUrl ? <UserOutlined /> : undefined}
              style={{
                backgroundColor: profile?.avatarDataUrl ? 'transparent' : '#87d068',
                border: profile?.avatarDataUrl ? '1px solid #fff' : 'none',
              }}
            />
            {!compactToolbar && (
              <Text
                style={{
                  color: 'white',
                  maxWidth: 'min(160px, 36vw)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {profile?.displayName || user || 'Гость'}
              </Text>
            )}
          </div>
        </Dropdown>
      </div>
    </Header>
  );
};

export default AppHeader;


